import { MappedEnrichedEncounter } from 'APP/Services/Coredata/index.types'
import type { Profile } from '@dialogue/cms'
import type { Camelized } from 'humps'
import type { RawPost } from 'APP/Services/Mattermost/types'
import type {
  ConversationChannel,
  ConversationEpisode,
  ConversationMember,
  CreatedEpisode,
  FamilyAppointment,
  FlowType,
} from 'APP/Services/Silkroad/types'
import type {
  EnrichedPost,
  NewPendingPostData,
  NormalizedPostList,
  NormalizedPostListOrError,
  PendingPost,
} from 'APP/Lib/MessagingHelpers/types'
import type { EnrichedEpisode } from '@dialogue/coredata'
import type { Practitioner } from 'APP/Lib/EpisodeListHelpers'

export type Channel = {
  channelId: string
  order: string[]
  posts: Record<string, EnrichedPost>
  draft: {
    text?: string | null
    photo?: unknown | null
    localId?: string | null
  }
  pendingPosts: Record<number, PendingPost>
  pendingOrder: number[]
  lastFetchedPostId: string | null
  lastFetchedPostAt: number | null
  oldestFetchedPostAt?: number | null
  oldestFetchedPostId?: string | null
  fetchingPosts: boolean
  fetchingPostsError: string | null
  error: string | null
  missingData: boolean
  msgCount: number
  allMembers: string[]
  members?: unknown[]
  lastViewedAt: number
  header?: string | null
  active?: boolean
  archived?: boolean | null
  updateAt?: number
  allOlderPostsFetched?: boolean
  hasPostFailureError?: boolean
  hiddenPosts?: Record<string, EnrichedPost>
}

export type Episode = {
  id: string
  channelId: string
  createdAt?: string
  serviceId?: string
  healthIssueTypeId?: string
  botStatus?: string
  outcomeId?: string
  isArchivable?: boolean
  archived?: boolean | null
  goBack?: {
    error: unknown | null
    running: boolean
  }
  /**
   * The Rasa command that was sent in the episode upon creation, if any.
   * This is only set for episodes created within the current user session.
   * @deprecated Use `flowType` instead when possible.
   */
  commandPost?: string
  /**
   * The flow type that was triggered in the episode upon creation, if any.
   * This is only set for episodes created within the current user session.
   */
  flowType?: FlowType
}

// TODO: exchange names with FileUploadStatusEnum, they should be the other way around
export enum ChannelUploadStatusEnum {
  RUNNING = 'running',
  DONE = 'done',
  ERROR = 'error',
}

export type ChannelUploadStatus = ChannelUploadStatusEnum | null

// TODO: exchange names with ChannelUploadStatusEnum, they should be the other way around
export enum FileUploadStatusEnum {
  UPLOADING = 'uploading',
  UPLOADED = 'uploaded',
  FAILED = 'failed',
  REMOVING_FILE = 'removing',
}

export type ChannelUploadSnapshot = {
  status?: FileUploadStatusEnum | null
  fileId?: string | null
  fileInfo?: { id: string } | null
  error?: string | null
}

export interface NormalizedAppointmentChild {
  id: number
  givenName?: string | null
}

export interface NormalizedAppointmentPractitioner {
  emr_id?: number
  id?: number
  job_title?: string
  picture?: string
}

export interface NormalizedAppointment extends FamilyAppointment {
  child?: NormalizedAppointmentChild
  practitioner?: NormalizedAppointmentPractitioner
}

export interface AppointmentsHistory {
  past: NormalizedAppointment[]
  upcoming: NormalizedAppointment[]
}

export type HistoryState = {
  channels: Record<string, Channel>
  hasUnreadChannels: boolean
  createEpisodeError: string | null
  createEpisodeRunning: boolean
  currentChannelId: string | null
  internalNavigationEpisodeId: string | null
  encounters: Record<string, MappedEnrichedEncounter>
  completedCarePlans: unknown[]
  episodes: Record<string, Episode>
  episode: Record<string, unknown>
  episodeMembers: Record<string, unknown>
  fetchEpisodeRequestRunning: boolean
  fetchEpisodeError: string | null
  fetchChannelsRunning: boolean
  fetchChannelsError: string | null
  practitioners: Record<string, Practitioner>
  fetchPractitionersError: string | null
  fetchPractitionersRunning: boolean
  orderedEpisodes: string[]
  undonePostIds: string[]
  posts: Record<string, unknown>
  practitionerBio: Camelized<Profile> | null
  fetchPractitionerBioError: { data: string } | null
  fetchPractitionerBioRunning: boolean
  mmSocketNotReady: boolean
  inAppNotificationPostId: string | null
  inAppNotificationChannelId?: string
  failedPostCounters: Record<string, unknown>
  hasPostFailureError: boolean
  appointments: AppointmentsHistory
  fetchAppointmentsRunning: boolean
  fetchAppointmentsError: string | null
  rescheduleAppointmentRunning: boolean
  rescheduleAppointmentError: string | null
  cancelAppointmentRunning: boolean
  cancelAppointmentError: string | null
  cancelAppointmentFromCalendarFailure: string | null
  cancelAppointmentFromCalendarRunning: boolean
  cancelAppointmentFromCalendarSuccess: boolean
  link: string | null
  linkContext: unknown | null
  linkError: string | null
  typingEvent: unknown | null
  archiveEpisodeRunning: boolean
  archiveEpisodeError: string | null
  fileUploads: Record<string, ChannelUploadStatus>
  shouldIgnoreAppStateChanges: boolean
  mattermostLongPollRunning: boolean
  uploads: Record<string, ChannelUploadSnapshot>
}

// Action payloads

export type ArchiveEpisodePayload = {
  /** The ID of the episode. */
  channelId: string
  /** Sprig analytics metadata to track the archival. */
  sprigSurveyData?: {
    eventProperties: SprigProperties
    trackName: string
  }
}

export type CancelAppointmentPayload = {
  /** The appointment to cancel. */
  appointment: NormalizedAppointment
}

export type CreateEpisodeSuccessPayload = {
  /**
   * The Rasa command that was launched in the episode after creation, if any.
   * @deprecated This is being replaced by `flowType`.
   */
  commandPost?: string
  /** The data of the episode that was created. */
  episode: CreatedEpisode | EnrichedEpisode
  /**
   * The flow type triggered in the episode after creation, if any.
   */
  flowType?: FlowType
  /** Indicates if the member has granted location permissions. */
  locationPermission: boolean
}

export type FetchAllPostsForChannelPayload = {
  /** The ID of the channel. */
  channelId: string
  /** The maximum number of posts to fetch. */
  limit?: number
}

export type FetchChannelsPayload = {
  /** If all historical channels posts should be fetched (within set limits). */
  bootstrap?: boolean
}

export type FetchChannelsSuccessPayload = {
  /* The data fetched for the channels. */
  channelSets: {
    channelId: string
    channelInfo?: ConversationChannel
    memberInfo?: ConversationMember
    postData?: NormalizedPostListOrError
  }[]
  /* The episodes data fetched. */
  memberEpisodes: ConversationEpisode[]
}

export type FetchEpisodePayload = {
  /** The ID of the episode. */
  channelId: string
}

export type FetchEpisodeSuccessPayload = {
  /** The episodes data fetched. */
  episodes: ConversationEpisode[]
}

export type FetchEpisodeMembersPayload = {
  /** The ID of the episode. */
  channelId: string
}

export type FetchNewPostsForChannelPayload = {
  /** The ID of the channel. */
  channelId: string
}

export type FetchOlderPostsForChannelPayload = {
  /** The ID of the channel. */
  channelId: string
}

export type FetchPostsSuccessPayload = {
  /** The ID of the channel. */
  channelId: string
  /** If the fetch operation retrieved all historical data. */
  fullFetch?: boolean
  /** The data obtained from the fetch. */
  postData: NormalizedPostList
}

export type FetchPractitionerBioPayload = {
  /** The Dialogue ID of the member. TODO: investigate how this is used & if this is needed */
  profileId: string
  /** The Dialogue ID of the practitioner. */
  userAppId: string
}

export type GoBackInEpisodePayload = {
  /** The ID of the episode. */
  channelId: string
  /** The ID of the post to go back on. */
  postId: string
}

export type LaunchEpisodeCommandPayload = {
  /**
   * The command to send. Including a slots payload is supported.
   * @deprecated Use flowType instead when possible
   */
  command: string
  /**
   * The flow type to trigger in the episode.
   */
  flowType?: FlowType
  /** The issue type to set on the episode. */
  healthIssueTypeId?: string
}

export type StartEpisodeWithFlowPayload = {
  /** The flow type to start. */
  flowType: FlowType
  /** The issue type to set on the episode. */
  healthIssueTypeId: string
}

export type ReceivePostsPayload = {
  /** The ID of the channel. */
  channelId: string
  /** If the posts data was obtained through a fetch. */
  fetched?: boolean
  /** If the fetch operation retrieved all historical data. */
  fullFetch?: boolean
  /** The data obtained. */
  postData: NormalizedPostList
}

export type RemovePendingPostPayload = {
  /** The ID of the channel. */
  channelId: string
  /** The local ID of the post to retry. */
  postId: number
}

export type RemoveUploadedFilePayload = {
  /** The ID of the channel. */
  channelId: string
  /** The ID of the file to remove. */
  fileId: string
}

export type RescheduleAppointmentPayload = {
  /** The appointment to reschedule. */
  appointment: NormalizedAppointment
}

export type RetryPostPayload = {
  /** The ID of the channel. */
  channelId: string
  /** The local ID of the post to retry. */
  postId: number
}

export type SetInAppNotificationDataPayload = {
  /** The ID of the channel where the new post was received. */
  channelId?: string
  /** The ID of the new post that was received. */
  postId?: string
}

export type SetFailedPostErrorPayload = {
  /** The ID of the channel. */
  channelId: string
  /** If the error should be surfaced to the user. */
  showPostFailureError: boolean
}

export type SubmitPostPayload = {
  answer?: NewPendingPostData['answer']
  /** The ID of the channel. */
  channelId: string
  fileObj?: NewPendingPostData['file']
  /** Arbitrary data to include in the post's `props`. */
  flags?: Record<string, unknown>
  payload?: NewPendingPostData['payload']
  text?: NewPendingPostData['message']
  /** The timestamp when the post was created locally. */
  timeStamp: number
  /** The Mattermost ID of the post author. */
  userId: string
}

export type SubmitPostFailurePayload = {
  /** The ID of the channel. */
  channelId: string
  /** The local ID of the post - to reconcile against pendingPosts and pendingOrder. */
  localId: number
  /** The error message, if any. */
  message?: string
}

export type SubmitPostSuccessPayload = {
  /** The ID of the channel. */
  channelId: string
  /** The post object returned from the server. */
  post: RawPost
}

export type UpdateChannelLastViewedPayload = {
  /** The ID of the channel. */
  channelId: string
}
